<template>
    <!-- selection container -->
    <div
        v-if="!genderOrDisciplineUrlParamWasProvided"
        class="max-tablet:flex-col max-tablet:gap-3 max-mobile:flex-nowrap mb-5 flex flex-wrap gap-5"
    >
        <div class="flex flex-col gap-1">
            <div class="text-sm">{{ getStrByLng().LABEL_SELECT_DISCIPLINE }}</div>
            <WtcMultiSelect
                v-model="selectedDisciplines"
                class="flex w-72 cursor-pointer items-center border border-black p-2"
                :empty-message="getStrByLng().MESSAGE_NO_DISCIPLINES"
                expandable-options
                :max-display-options="3"
                :options="groupedDisciplinesList"
                :placeholder="getStrByLng().LABEL_ALL_DISCIPLINES"
                :pt="{
                    overlay: 'bg-white border border-black/50 mt-1 max-mobile:w-full x--theme-scrollbar',
                    dropdown: 'ml-2',
                    root: 'border-content inline-flex items-center justify-between border bg-white px-2 py-1 cursor-pointer select-none',
                    list: 'flex gap-6 text-sm p-3 py-2 pr-5 max-tablet:flex-col max-tablet:gap-2 max-tablet:px-2',
                    labelContainer: 'overflow-hidden',
                    label: 'overflow-hidden text-ellipsis whitespace-nowrap',
                    listContainer: 'max-laptop:!max-h-none !max-h-[26rem]',
                    optionGroup:
                        'border-l border-black/10 first:border-none pl-4 first:pl-0 py-1 max-laptop:pl-0 max-laptop:border-l-0 max-tablet:border-t max-tablet:pt-3 max-laptop:first:pt-1',
                    wtcList: '!grid-rows-10 max-laptop:grid-flow-row'
                }"
            >
                <template #group-icon="{ allSelected, someSelected }">
                    <img :src="allSelected ? checkActive : someSelected ? checkPartial : checkEmpty" />
                </template>
                <template #item-icon="{ isSelected }">
                    <img :src="isSelected ? checkActive : checkEmpty" />
                </template>
            </WtcMultiSelect>
        </div>
    </div>
    <!-- content -->
    <div>
        <WdlArchiveFetch
            :is-data-available="diamondRaceData !== undefined"
            :is-fetching="isFetchingDiamondRaceData"
            :place-holder-msg="getStrByLng().MESSAGE_NO_STANDINGS"
        >
            <div class="max-tablet-xl:grid-cols-1 grid grid-cols-2 gap-3">
                <!-- Men -->
                <StandingsTable
                    v-for="discipline in filteredMenDisciplineList"
                    :key="discipline.DCode"
                    :discipline="discipline"
                    :hide-header="providedHeaderUrlParam === 'false'"
                />
                <!-- Women -->
                <StandingsTable
                    v-for="discipline in filteredWomenDisciplineList"
                    :key="discipline.DCode"
                    :discipline="discipline"
                    :hide-header="providedHeaderUrlParam === 'false'"
                />
            </div>
        </WdlArchiveFetch>
    </div>
</template>

<script setup lang="ts">
import { useOwlStore } from "@swisstiming/owl-core";
import { getStrByLng, IWtcMultiSelectItem, IWtcMultiSelectOptions, WtcMultiSelect } from "@swisstiming/webtec-kit";
import { AfterFetchContext, useFetch } from "@vueuse/core";
import { computed, ref } from "vue";

import checkActive from "../assets/images/check-active.svg";
import checkEmpty from "../assets/images/check-empty.svg";
import checkPartial from "../assets/images/check-partial.svg";
import StandingsTable from "../components/StandingsTable.vue";
import WdlArchiveFetch from "../components/WdlArchiveFetch.vue";
import { IDiamondRaceJson } from "../interfaces/wdl-archive.interfaces";
import { useArchiveStore } from "../stores/wdl-archive.store";

const owlStore = useOwlStore();
const archiveStore = useArchiveStore();

// if url-param is provided display corresponding discipline-header
const providedHeaderUrlParam = computed(() => owlStore.qParams?.header as "false");

const selectedSeason = ref<string>();
// if url-param is provided use param otherwise the default selected season (latest)
const relevantSeason = computed(() => (owlStore.qParams?.season ?? selectedSeason.value)?.toLowerCase?.());

// if url-param is provided display corresponding gender
const providedGenderUrlParam = computed(() => (owlStore.qParams?.gender as "m" | "w")?.toLowerCase());

const selectedDisciplines = ref<IWtcMultiSelectItem[]>([]);
// if url-param is provided display corresponding discipline
const providedDisciplineUrlParam = computed(() => owlStore.qParams?.discipline);

// if url-param is provided use param otherwise the selection
const relevantDisciplines = computed<IWtcMultiSelectItem[]>(() =>
    providedDisciplineUrlParam.value
        ? [{ label: undefined, value: owlStore.qParams?.discipline }]
        : selectedDisciplines.value
);

const genderOrDisciplineUrlParamWasProvided = computed(
    () => providedGenderUrlParam.value || providedDisciplineUrlParam.value
);

// seasons.json
useFetch(archiveStore.seasonsJsonUrl, {
    refetch: true,
    afterFetch(ctx: AfterFetchContext<string[]>) {
        // preselect latest season as default
        selectedSeason.value = ctx.data?.at(-1);
        return ctx;
    }
}).json<string[]>();

// diamond_race.json
const { data: diamondRaceData, isFetching: isFetchingDiamondRaceData } = useFetch(
    computed(() =>
        // if 'season'-param is provided use it, else use the latest season from the 'seasons.json'-request
        archiveStore.getDiamondRaceJsonUrl(relevantSeason.value)
    ),
    {
        refetch: true,
        updateDataOnError: true,
        onFetchError(ctx) {
            // reset selections if requested data is not available
            selectedDisciplines.value = [];
            ctx.data = undefined;
            return ctx;
        }
    }
).json<IDiamondRaceJson>();

const womenDisciplineList = computed(() => Object.values(diamondRaceData.value?.Data?.Women ?? {}));
const menDisciplineList = computed(() => Object.values(diamondRaceData.value?.Data?.Men ?? {}));
const groupedDisciplinesList = computed<IWtcMultiSelectOptions[]>(() => [
    {
        label: getStrByLng().LABEL_ALL_MEN_DISCIPLINES,
        items: menDisciplineList.value?.map((discipline) => {
            return {
                label: discipline.Name,
                value:
                    // 'DCode' is gender-unspecific, 'Code' is gender-specific
                    genderOrDisciplineUrlParamWasProvided.value ? discipline.DCode : discipline.Code
            };
        })
    },
    {
        label: getStrByLng().LABEL_ALL_WOMEN_DISCIPLINES,
        items: womenDisciplineList.value?.map((discipline) => {
            return {
                label: discipline.Name,
                value:
                    // 'DCode' is gender-unspecific, 'Code' is gender-specific
                    genderOrDisciplineUrlParamWasProvided.value ? discipline.DCode : discipline.Code
            };
        })
    }
]);

const selectedDisciplineValues = computed(() => relevantDisciplines.value.map((discipline) => discipline.value));
const filteredWomenDisciplineList = computed(() => {
    if (genderOrDisciplineUrlParamWasProvided.value) {
        if (providedGenderUrlParam.value === "m") {
            return [];
        }

        if (providedDisciplineUrlParam.value) {
            return womenDisciplineList.value.filter((discipline) =>
                // 'DCode' is the gender-unspicific discipline-code provided to filter disciplines
                selectedDisciplineValues.value.includes(discipline.DCode)
            );
        }

        return womenDisciplineList.value;
    }

    // return the complete list if no disciplines are selected
    if (relevantDisciplines.value.length <= 0) {
        return womenDisciplineList.value;
    }

    // 'Code' is the gender-specific event-name used as discipline-code to filter disciplines
    return womenDisciplineList.value.filter((discipline) => selectedDisciplineValues.value.includes(discipline.Code));
});
const filteredMenDisciplineList = computed(() => {
    if (genderOrDisciplineUrlParamWasProvided.value) {
        if (providedGenderUrlParam.value === "w") {
            return [];
        }

        if (providedDisciplineUrlParam.value) {
            return menDisciplineList.value.filter((discipline) =>
                // 'DCode' is the gender-unspicific discipline-code provided to filter disciplines
                selectedDisciplineValues.value.includes(discipline.DCode)
            );
        }

        return menDisciplineList.value;
    }

    // return the complete list if no disciplines are selected
    if (relevantDisciplines.value.length <= 0) {
        return menDisciplineList.value;
    }

    // 'Code' is the gender-specific event-name used as discipline-code to filter disciplines
    return menDisciplineList.value.filter((discipline) => selectedDisciplineValues.value.includes(discipline.Code));
});
</script>
